import React from 'react';
import { Mail, CalendarClock, Phone } from 'lucide-react';
import { useSupabaseResource } from '@/hooks/useSupabaseResource';

const columnClasses = 'px-4 py-3 text-sm text-stone-800 align-top';

const renderSource = (source) => {
  if (!source) return 'Unknown';
  return source
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const ContactMessagesModule = () => {
  const { records, loading, error, refresh, deleteRecord } = useSupabaseResource('contact_messages', {
    order: 'created_at',
    ascending: false,
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this message?')) return;
    try {
      await deleteRecord(id);
    } catch (err) {
      console.error('Failed to delete message', err);
      alert('Failed to delete message. Please try again.');
    }
  };

  return (
    <section className="rounded-2xl border border-stone-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-stone-100 px-6 py-4">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">Contact Messages</h2>
          <p className="text-sm text-stone-500">
            View every inquiry from the contact pages and property detail forms.
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center rounded-lg border border-stone-200 px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50"
        >
          <Mail className="mr-1.5 h-4 w-4" />
          Refresh
        </button>
      </header>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-stone-200 text-left">
          <thead className="bg-stone-50 text-xs font-semibold uppercase tracking-wide text-stone-500">
            <tr>
              <th className={columnClasses}>Name</th>
              <th className={columnClasses}>Contact</th>
              <th className={columnClasses}>Message</th>
              <th className={columnClasses}>Property</th>
              <th className={columnClasses}>Source</th>
              <th className={columnClasses}>Received</th>
              <th className={columnClasses}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100 bg-white">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-stone-500">
                  Loading messages...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-red-500">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && records.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-stone-500">
                  No messages yet.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              records.map((message) => (
                <tr key={message.id} className="border-b border-stone-100">
                  <td className={columnClasses}>
                    <p className="font-medium text-stone-900">{message.name || 'Anonymous'}</p>
                    {message.email && (
                      <a
                        href={`mailto:${message.email}`}
                        className="text-xs text-lime-600 hover:underline"
                      >
                        {message.email}
                      </a>
                    )}
                  </td>
                  <td className={columnClasses}>
                    {message.phone ? (
                      <a
                        href={`tel:${message.phone}`}
                        className="inline-flex items-center gap-1 text-stone-700 hover:text-lime-600"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        {message.phone}
                      </a>
                    ) : (
                      <span className="text-stone-400">No phone</span>
                    )}
                  </td>
                  <td className={columnClasses}>
                    <p className="text-stone-700 whitespace-pre-wrap">{message.message || 'No message'}</p>
                  </td>
                  <td className={columnClasses}>
                    {message.property_id ? (
                      <a
                        href={`/property/${message.property_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-lime-600 hover:underline"
                      >
                        #{message.property_id}
                      </a>
                    ) : (
                      <span className="text-stone-400">General inquiry</span>
                    )}
                  </td>
                  <td className={columnClasses}>{renderSource(message.source)}</td>
                  <td className={`${columnClasses} text-sm text-stone-500`}>
                    {message.created_at ? (
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="h-3.5 w-3.5" />
                        {new Date(message.created_at).toLocaleString()}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(message.id)}
                      className="text-xs font-medium text-red-500 hover:text-red-600"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default ContactMessagesModule;
